import React, { createContext, useContext } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useClient } from '@/stores/useClient.ts'
import { AlbumFile } from '@/models/albumFile.ts'
import { useParams } from 'react-router-dom'

export interface ExampleImageContextValue {
  exampleImages: AlbumFile[]
  isLoading: boolean
  error: Error | null
  refetch: () => Promise<any>
}

export const ExampleImageContext = createContext<ExampleImageContextValue | null>(null)

export function ExampleImageProvider({ children }: { children: React.ReactNode }) {
  const { getPreviewExamples } = useClient()
  const { driveId } = useParams<{ driveId: string }>()
  
  const queryKey = ['previewExamples', driveId]
  
  const { data, isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!driveId) {
        return []
      }
      const res = await getPreviewExamples(driveId)
      return res.data as AlbumFile[]
    },
    enabled: !!driveId,
  })
  
  return (
    <ExampleImageContext.Provider
      value={{
        exampleImages: data || [],
        isLoading,
        error: error as Error | null,
        refetch,
      }}
    >
      {children}
    </ExampleImageContext.Provider>
  )
}

export function useExampleImages() {
  const context = useContext(ExampleImageContext)
  if (!context) {
    throw new Error('useExampleImages must be used within ExampleImageProvider')
  }
  return context
}